import React from "react";

/**
 * Stylized iPhone mockup showing where a sponsor's ads appear in the club's fan app.
 * Two phones side-by-side: Home view + Internal page view, with active placements highlighted in tenant colour.
 */
export default function PhoneMockup({ ads, sponsorName }) {
    if (!ads) return null;
    const placements = new Set(ads.placements || []);
    const showsAny = placements.size > 0;
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 items-start" data-testid="phone-mockup">
            <Phone label="Home screen" testId="mockup-home">
                {placements.has("home_banner") && <AdBlock label="Banner" testId="mockup-home-banner" sponsor={sponsorName} variant="banner" />}
                <FauxRow />
                <FauxRow tall />
                {placements.has("home_rectangle") && <AdBlock label="Rectangle" testId="mockup-home-rect" sponsor={sponsorName} variant="rectangle" />}
                <FauxRow />
                <FauxRow />
            </Phone>
            <Phone label="Internal page" testId="mockup-internal">
                <FauxHeader />
                {placements.has("internal_banner") && <AdBlock label="Banner" testId="mockup-internal-banner" sponsor={sponsorName} variant="banner" />}
                <FauxRow />
                <FauxRow />
                <FauxRow tall />
                <FauxRow />
            </Phone>
            {placements.has("internal_interstitial") && (
                <div className="sm:col-span-2 border p-4 flex items-start gap-3" style={{ borderColor: "var(--tenant-primary)", background: "rgba(255,255,255,0.02)" }}
                    data-testid="mockup-interstitial">
                    <div className="w-8 h-8 border flex items-center justify-center text-[9px] font-bold tracking-tight uppercase shrink-0"
                        style={{ borderColor: "var(--tenant-primary)", color: "var(--tenant-primary)" }}>FS</div>
                    <div className="text-xs text-zinc-300 leading-relaxed">
                        <strong className="text-white block">Full-screen interstitial</strong>
                        Your ad takes over the whole screen between page transitions. The most attention-grabbing placement — used sparingly so fans don't disengage.
                    </div>
                </div>
            )}
            {!showsAny && (
                <div className="sm:col-span-2 text-center text-xs text-zinc-500 py-6 border hairline">No specific placements configured yet.</div>
            )}
        </div>
    );
}

const Phone = ({ label, children, testId }) => (
    <div className="flex flex-col items-center" data-testid={testId}>
        <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-2">{label}</div>
        <div className="relative w-[180px] sm:w-[210px]">
            <div className="border-2 border-white/10 rounded-[28px] p-2 bg-[rgba(255,255,255,0.02)]">
                <div className="rounded-[20px] bg-[#0a0a0a] aspect-[9/19] overflow-hidden border border-white/[0.04] flex flex-col">
                    {/* notch */}
                    <div className="h-5 flex items-center justify-center">
                        <div className="w-12 h-1 bg-white/20 rounded-full" />
                    </div>
                    <div className="flex-1 flex flex-col gap-1.5 p-2 overflow-hidden">
                        {children}
                    </div>
                </div>
            </div>
        </div>
    </div>
);

const AdBlock = ({ label, sponsor, variant, testId }) => {
    const isBanner = variant === "banner";
    return (
        <div
            className="border flex items-center justify-center px-2 text-center relative"
            style={{
                borderColor: "var(--tenant-primary)",
                background: "var(--tenant-primary)",
                color: "var(--tenant-primary-foreground, #fff)",
                height: isBanner ? "26px" : "60px",
            }}
            data-testid={testId}
        >
            <div className="text-[8px] font-bold uppercase tracking-wider truncate">{sponsor || "Sponsor"}</div>
            <div className="absolute -top-1.5 -right-1 text-[7px] uppercase tracking-wider px-1 py-0.5"
                style={{ background: "var(--sb-amber)", color: "#000" }}>
                {label}
            </div>
        </div>
    );
};

const FauxRow = ({ tall }) => <div className={`bg-white/[0.04] rounded-sm ${tall ? "h-12" : "h-5"}`} />;
const FauxHeader = () => (
    <div className="bg-white/[0.06] h-7 flex items-center px-2 gap-1.5">
        <div className="w-2 h-2 bg-white/20" />
        <div className="h-1.5 w-16 bg-white/15 rounded-full" />
    </div>
);
